// Configuration for Conference Badges, QR Code parameters, Founder profile, and Booking links

export interface FounderConfig {
  name: string;
  title: string;
  credentials: string;
  bio: string;
  linkedinUrl: string;
  bookingUrl: string;
  email: string;
}

export interface ConferenceConfig {
  defaultName: string;
  dates: string;
  venue: string;
  location: string;
  tagline: string;
  showcaseArea: string;
  description: string;
  supportedTimeParams: string[];
}

export const FOUNDER_CONFIG: FounderConfig = {
  name: "BJ Muse",
  title: "Founder & Chief Infrastructure Architect",
  credentials: "30-Year Hyperscale Network Veteran (AOL, Equifax, Google)",
  bio: "Specializing in multi-vendor rural broadband telemetry, alert storm suppression, and vendor-independent network architectures.",
  // Can be configured via environment variables or changed here directly
  linkedinUrl: (import.meta as any).env?.VITE_LINKEDIN_URL || "https://www.linkedin.com/in/bjmuse",
  bookingUrl: (import.meta as any).env?.VITE_BOOKING_URL || "https://calendar.app.google/qHeWeoqrHs3F82AZ8",
  email: (import.meta as any).env?.VITE_FOUNDER_EMAIL || "bj@musetelemetry.com"
};

export const CONFERENCE_CONFIG: ConferenceConfig = {
  defaultName: "Fall Conference 2026",
  dates: "September 13–16, 2026",
  venue: "Gaylord Opryland Resort & Convention Center",
  location: "Nashville, TN",
  tagline: "Future-Focused Network Telemetry for Rural Broadband Providers",
  showcaseArea: "Solutions Showcase",
  description: "Where rural broadband providers come to navigate emerging technologies and evaluate efficient, scalable operational solutions.",
  supportedTimeParams: ["t", "time", "ts", "badge", "epoch", "badge_t"]
};

export interface AttendeeInterestOption {
  value: string;
  label: string;
  badge: string;
  summary: string;
}

export interface RoleSegmentOption {
  value: string;
  label: string;
  shortRole: string;
}

export const ROLE_SEGMENT_OPTIONS: RoleSegmentOption[] = [
  { value: "ISP Owner / CEO / GM", label: "ISP Owner / CEO / GM", shortRole: "Executive / Owner" },
  { value: "CTO / VP Engineering", label: "CTO / VP Engineering", shortRole: "Engineering Leadership" },
  { value: "NOC Manager / Operations Lead", label: "NOC Lead / Operations", shortRole: "NOC / Ops" },
  { value: "Principal Network Architect / Engineer", label: "Network Architect / Engineer", shortRole: "Architecture & Engineering" },
  { value: "BEAD / ReConnect / Grantee", label: "BEAD / Grant Infrastructure Lead", shortRole: "Grant / Compliance" },
  { value: "Consultant / Vendor / Peer", label: "Consultant / Industry Peer", shortRole: "Industry Partner" }
];

export const ATTENDEE_INTEREST_OPTIONS: AttendeeInterestOption[] = [
  {
    value: "rural_cost_consolidation",
    label: "Rural Broadband CEO / GM: Cut multi-tool fees & protect operating margins",
    badge: "Stack Consolidation",
    summary: "Single-pane telemetry replacing 2-3 overlapping vendor licenses with predictable per-subscriber pricing for independent operators."
  },
  {
    value: "rural_truck_roll_slashing",
    label: "NOC & Operations Lead: Slash wasted truck rolls with root-cause clustering",
    badge: "Outage Clustering",
    summary: "Topological correlation collapses 200+ cascading customer drops into 1 upstream incident, proving whether the fault is in distribution fiber or customer CPE."
  },
  {
    value: "forge_legacy_onboarding",
    label: "Lead Network Engineer: Onboard mixed fiber, wireless & legacy gear",
    badge: "MUSE Forge AI Engine",
    summary: "Paste raw CLI/SSH terminal output from any vendor to generate production parsers and metric schemas in 60 seconds."
  },
  {
    value: "bead_reconnect_reporting",
    label: "BEAD / ReConnect Grantee: Unified reporting across mixed-vendor builds",
    badge: "Grant & Compliance",
    summary: "Decouple monitoring from hardware purchases. Mix Calix, Nokia, Cisco, and legacy equipment with audit-ready SLA reporting."
  },
  {
    value: "solutions_showcase_meeting",
    label: "Solutions Showcase: 15-Minute Operational Review in Nashville",
    badge: "1-on-1 Review",
    summary: "Meet directly with BJ Muse at Gaylord Opryland to review your current hardware mix or recent outage on a live single screen."
  }
];

export const PRODUCT_VALUE_PILLARS = [
  {
    title: "One Pane of Glass",
    desc: "Unified DOCSIS, PON, GPON, Arista, Cisco console.",
    metric: "0 Lock-In"
  },
  {
    title: "Predictable Pricing",
    desc: "Per-subscriber model. Scales with revenue.",
    metric: "Predictable Margins"
  },
  {
    title: "Root-Cause Clustering",
    desc: "Isolates upstream faults. Slashes false truck rolls.",
    metric: "Slashing MTTR"
  }
];
